import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router";
import { CheckCircle2, Coins, Home, ListChecks, Settings, Sparkles, Store, Target, User, Wrench } from "lucide-react";
import { AppShell } from "./components/AppShell";
import { KidSwitcher } from "./components/KidSwitcher";
import { BackToParent } from "./components/SwitchUser";
import { PageLoader } from "./components/ui";
import { isNative } from "./lib/native";
import { NativePushBridge } from "./lib/nativePush";
import { RequireRole } from "./lib/session";
import { useApprovals, useMe } from "./lib/queries";
import { approvalsForKid, useSelectedKid } from "./lib/selectedKid";
import { Landing } from "./pages/public/Landing";
import { ParentLogin } from "./pages/public/ParentLogin";
import { Signup } from "./pages/public/Signup";
import { KidLogin } from "./pages/public/KidLogin";
import { JoinFamily } from "./pages/public/JoinFamily";
import { Setup } from "./pages/parent/Setup";
import { BillingPage } from "./pages/parent/Billing";
import { ConnectPage } from "./pages/parent/Connect";
import { ForgotPassword, ResetPassword } from "./pages/public/PasswordReset";
import { LegalPage } from "./pages/public/LegalPage";
import { ConsentPage, VerifyEmailPage } from "./pages/public/Consent";
import { PrivacySecurity } from "./pages/parent/Privacy";
import { ParentHome } from "./pages/parent/Home";
import { Approvals } from "./pages/parent/Approvals";
import { KidRoute, KidView } from "./pages/parent/KidDetail";
import { Plan } from "./pages/parent/Plan";
import { ResponsibilitiesEditor } from "./pages/parent/Responsibilities";
import { JobsEditor } from "./pages/parent/Jobs";
import { RewardsEditor } from "./pages/parent/Rewards";
import { StoreEditor } from "./pages/parent/Store";
import { Discipline } from "./pages/parent/Discipline";
import { Advisor } from "./pages/parent/Advisor";
import { More } from "./pages/parent/More";
import { FamilySettings } from "./pages/parent/FamilySettings";
import { Integrations } from "./pages/parent/Integrations";
import { Notifications } from "./pages/shared/Notifications";
import { KidToday } from "./pages/kid/Today";
import { KidJobs } from "./pages/kid/Jobs";
import { KidShop } from "./pages/kid/Shop";
import { KidScreenTimeBar } from "./components/ScreenTime";
import { KidGoals } from "./pages/kid/Goals";
import { KidMe } from "./pages/kid/Me";
import { LittleMe, LittleShop, LittleToday } from "./pages/kid/Little";
import { OlderToday } from "./pages/kid/Older";
import { useScreenLevel } from "./pages/kid/common";

function ParentLayout() {
  const { data } = useApprovals();
  const kid = useSelectedKid();
  return (
    <AppShell
      notificationsTo="/parent/notifications"
      switcher={(variant) => <KidSwitcher variant={variant} />}
      nav={[
        { to: "/parent", label: "Home", icon: <Home />, end: true },
        { to: "/parent/approvals", label: "Approvals", icon: <CheckCircle2 />, badge: data && (kid ? approvalsForKid(data, kid.id).count : data.count) },
        { to: "/parent/plan", label: "Plan", icon: <Wrench /> },
        { to: "/parent/advisor", label: "Advisor", icon: <Sparkles /> },
        { to: "/parent/more", label: "Settings", icon: <Settings /> },
      ]}
    />
  );
}

/** Parent Home: the whole family, or the kid picked in the kid switcher. */
function ParentIndex() {
  const kid = useSelectedKid();
  return kid ? <KidView key={kid.id} kidId={kid.id} /> : <ParentHome />;
}

function KidLayout() {
  const level = useScreenLevel();
  return (
    <AppShell
      notificationsTo="/kid/notifications"
      banner={<KidScreenTimeBar />}
      switcher={(variant) => <BackToParent variant={variant} />}
      bigTabs={level === "little"}
      nav={
        level === "little"
          ? [
              { to: "/kid", label: "Today", icon: <ListChecks />, end: true },
              { to: "/kid/shop", label: "Shop", icon: <Store /> },
              { to: "/kid/me", label: "Me", icon: <User /> },
            ]
          : [
              { to: "/kid", label: "Today", icon: <ListChecks />, end: true },
              { to: "/kid/jobs", label: "Jobs", icon: <Coins /> },
              { to: "/kid/shop", label: "Shop", icon: <Store /> },
              { to: "/kid/goals", label: "Goals", icon: <Target /> },
              { to: "/kid/me", label: "Me", icon: <User /> },
            ]
      }
    />
  );
}

/** Today by level. Little kids have everything on this one screen. */
function KidTodayRoute() {
  const level = useScreenLevel();
  return level === "little" ? <LittleToday /> : level === "older" ? <OlderToday /> : <KidToday />;
}

function KidShopRoute() {
  return useScreenLevel() === "little" ? <LittleShop /> : <KidShop />;
}

function KidMeRoute() {
  return useScreenLevel() === "little" ? <LittleMe /> : <KidMe />;
}

/** Jobs and goals live on Today for little kids; old links land there. */
function NotForLittle({ children }: { children: ReactNode }) {
  return useScreenLevel() === "little" ? <Navigate to="/kid" replace /> : children;
}

function HomeRedirect() {
  const { data, isLoading } = useMe();
  if (isLoading) return <PageLoader />;
  if (data?.user.role === "parent") return <Navigate to="/parent" replace />;
  if (data?.user.role === "kid") return <Navigate to="/kid" replace />;
  return <Landing />;
}

export function App() {
  return (
    <>
      {isNative() && <NativePushBridge />}
    <Routes>
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/login" element={<ParentLogin />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/kid-login" element={<KidLogin />} />
      <Route path="/join/:code" element={<JoinFamily />} />
      <Route path="/join" element={<JoinFamily />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={<LegalPage doc="privacy" />} />
      <Route path="/terms" element={<LegalPage doc="terms" />} />
      <Route path="/consent" element={<ConsentPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      <Route
        path="/connect"
        element={
          <RequireRole role="parent">
            <ConnectPage />
          </RequireRole>
        }
      />
      <Route
        path="/parent/billing"
        element={
          <RequireRole role="parent">
            <BillingPage />
          </RequireRole>
        }
      />
      <Route
        path="/parent/setup"
        element={
          <RequireRole role="parent">
            <Setup />
          </RequireRole>
        }
      />
      <Route
        path="/parent"
        element={
          <RequireRole role="parent">
            <ParentLayout />
          </RequireRole>
        }
      >
        <Route index element={<ParentIndex />} />
        <Route path="approvals" element={<Approvals />} />
        <Route path="kids/:kidId" element={<KidRoute />} />
        <Route path="plan" element={<Plan />} />
        <Route path="plan/responsibilities" element={<ResponsibilitiesEditor />} />
        <Route path="plan/jobs" element={<JobsEditor />} />
        <Route path="plan/rewards" element={<RewardsEditor />} />
        <Route path="plan/store" element={<StoreEditor />} />
        <Route path="plan/discipline" element={<Discipline />} />
        <Route path="advisor" element={<Advisor />} />
        <Route path="more" element={<More />} />
        <Route path="family" element={<FamilySettings />} />
        <Route path="integrations" element={<Integrations />} />
        <Route path="privacy" element={<PrivacySecurity />} />
        <Route path="notifications" element={<Notifications />} />
      </Route>

      <Route
        path="/kid"
        element={
          <RequireRole role="kid">
            <KidLayout />
          </RequireRole>
        }
      >
        <Route index element={<KidTodayRoute />} />
        <Route path="jobs" element={<NotForLittle><KidJobs /></NotForLittle>} />
        <Route path="shop" element={<KidShopRoute />} />
        <Route path="goals" element={<NotForLittle><KidGoals /></NotForLittle>} />
        <Route path="me" element={<KidMeRoute />} />
        <Route path="notifications" element={<Notifications />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
